import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { ExportButton, ScopeButton } from '@monorepo/shared-lib';
import { Input, Skeleton, Space } from 'antd';
import { ReactComponent as IconBell } from 'assets/images/icons/iconBell.svg';
import { ReactComponent as IconNote } from 'assets/images/icons/iconNote.svg';
import ReconcileImage from 'assets/images/reconcile-image.svg';
import { StyledCard } from 'components/Custom/card';
import { FeatureDotBadge } from 'components/Custom/FeatureDotBadge';
import { ProviderIcon } from 'components/Custom/ProviderIcon';
import { ProviderLogoNav } from 'components/Custom/ProviderLogoNav';
import { QuestionMarkTooltip } from 'components/Custom/QuestionMarkTooltip';
import {
  CardDescSpan,
  CardTitleLargeSpan,
  CardTitleSpan,
} from 'components/Custom/spans';
import { ScopeTable } from 'components/Custom/table';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { ActivityLogs } from 'components/Vat/Report/activity-logs';
import { ConfirmBoxContainer } from 'containers/ConfirmBox';
import { MessageStates } from 'containers/MessageBox';
import { formatDisplayDate } from 'i18n/calendarLabels';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { VatControlAccountReconciliation } from 'shared/components/OrganisationDetails/vat-control-account-reconciliation';
import { useHistoryBlock } from 'hooks/useHistoryBlock';
import { AccountType } from 'store/api/enums';
import {
  turnDrawerOn,
  turnMessageOn,
  turnModalOff,
  turnModalOn,
} from 'store/app/actions';
import {
  AdvancedUserRightsCodes,
  DataLogStatus,
  DrawerType,
  EntityStatus,
  MenuSize,
  WindowType,
} from 'store/app/enums';
import { DrawerData, MessageData, ModalData } from 'store/app/types';
import { User } from 'store/auth/models';
import type { Mapping } from 'store/organisation/models';
import { getOrgMappedReturns } from 'store/organisation/selectors';
import {
  fetchReconciliationLogsRequest,
  resetReconciliation,
  saveReconciliationReportRequest,
  updateOrganisationTaxBalanceRequest,
  viewReconciliationReportRequest,
} from 'store/reconciliation/actions';
import type {
  ReconTotal,
  ViewReconciliationDataResponse,
} from 'store/reconciliation/models';
import {
  getReconciliationExceptionsSize,
  getReconciliationGenerating,
  getReconciliationLoading,
  getReconciliationLogs,
  getReconciliationLogsTotalElements,
  getReconciliationOthersSize,
  getReconciliationReportId,
  getReconciliationTotalTaxExceptions,
  getReconciliationTotalTaxOthers,
  getReconciliationTotalTaxTransactions,
  getReconciliationTransactionsSize,
  getSaveReconciliationReportLoading,
  getSaveReconciliationReportSuccess,
} from 'store/reconciliation/selectors';
import type { MappedReturn } from 'store/returns/models/returnData';
import {
  addNoteRequest,
  clearReconciliationRun,
  loadReconFailure,
} from 'store/vat/actions';
import type { VatReturnLog, VatRunData } from 'store/vat/models';
import { getVatLoading } from 'store/vat/selectors';
import type { ReconciliationRun } from 'store/vat/types';
import { hasAdvancedUserRight } from 'utils/hasAdvancedUserRight';
import { useNumberFormat } from 'utils/numberFormat';
import { PreferenceActions } from 'utils/preferences';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { StatusGetter } from 'utils/status-getter';
dayjs.extend(utc);

const { TextArea } = Input;

function reportingDateKey(value: string | null | undefined): string {
  if (value == null || value === '') return '';
  const s = String(value).trim();
  return s.length >= 10 ? s.slice(0, 10) : s;
}

/** Same resolution order as mappingId: open log id → dataLog uuid → period + return type (+ selectedMaps if ambiguous). */
function findSavedViewMappedReturnHit(args: {
  organisationId: string;
  mappedReturns: MappedReturn[];
  dataLog: VatReturnLog | null | undefined;
  openDataLogId: string;
  selectedMaps: Mapping[] | undefined;
}): MappedReturn | undefined {
  const {
    organisationId,
    mappedReturns,
    dataLog: dl,
    openDataLogId,
    selectedMaps,
  } = args;
  const rows = mappedReturns.filter((r) => r.organisationId === organisationId);
  if (!rows.length) return undefined;

  if (openDataLogId) {
    const hit = rows.find((r) => r.dataLog?.uuid === openDataLogId);
    if (hit) return hit;
  }

  if (dl?.uuid) {
    const hit = rows.find((r) => r.dataLog?.uuid === dl.uuid);
    if (hit) return hit;
  }

  if (dl?.fromReportingDate && dl?.toReportingDate) {
    const fromK = reportingDateKey(dl.fromReportingDate);
    const toK = reportingDateKey(dl.toReportingDate);
    const typeId =
      dl.taxReportTypeId != null && String(dl.taxReportTypeId).trim() !== ''
        ? String(dl.taxReportTypeId)
        : '';
    const periodMatches = rows.filter((r) => {
      if (!r.dataLog) return false;
      if (
        reportingDateKey(r.dataLog.fromReportingDate) !== fromK ||
        reportingDateKey(r.dataLog.toReportingDate) !== toK
      ) {
        return false;
      }
      if (!typeId) return true;
      return String(r.returnTypeId ?? '') === typeId;
    });
    if (periodMatches.length === 1) return periodMatches[0];
    if (periodMatches.length > 1 && selectedMaps?.length) {
      const allowed = new Set(selectedMaps.map((m) => m.mappingId));
      const narrowed = periodMatches.filter((r) => allowed.has(r.mappingId));
      if (narrowed.length === 1) return narrowed[0];
    }
  }

  return undefined;
}

type SavedViewOpenPeriodFromMappedReturns = {
  fromReportingDate: string | null | undefined;
  toReportingDate: string | null | undefined;
  dueDate: string | null | undefined;
};

function buildSavedViewOpenPeriodFromHit(
  hit: MappedReturn | undefined,
  selectedMaps: Mapping[] | undefined
): SavedViewOpenPeriodFromMappedReturns | undefined {
  if (!hit) return undefined;
  const dl = hit.dataLog;
  const fromReportingDate =
    dl?.fromReportingDate ?? hit.fromReportingDate ?? undefined;
  const toReportingDate =
    dl?.toReportingDate ?? hit.toReportingDate ?? undefined;

  const mapping = selectedMaps?.find((m) => m.mappingId === hit.mappingId);
  let dueDate: string | null | undefined =
    dl?.dueDate != null && String(dl.dueDate).trim() !== ''
      ? String(dl.dueDate)
      : undefined;
  if (dueDate == null && mapping?.returnDueDate != null) {
    const d = dayjs(mapping.returnDueDate);
    dueDate = d.isValid() ? d.format('YYYY-MM-DD') : undefined;
  }
  if (dueDate == null && mapping?.dueDate != null) {
    const d = dayjs(mapping.dueDate);
    dueDate = d.isValid() ? d.format('YYYY-MM-DD') : undefined;
  }
  if (dueDate == null && hit.nextDueDate) {
    const d = dayjs(hit.nextDueDate);
    dueDate = d.isValid() ? d.format('YYYY-MM-DD') : undefined;
  }

  return { fromReportingDate, toReportingDate, dueDate };
}

const EMPTY_RECON_TOTAL: ReconTotal = {
  openDataLogId: '',
  openTransactionsCount: 0,
  openExceptionsCount: 0,
  totalTaxOpenTransactions: 0,
  totalTaxOpenExceptions: 0,
  gtBalance: 0,
  xeroBalance: 0,
  difference: 0,
  totalTaxBalance: 0,
  account: '',
  savedStatus: null,
};

/** Saved-report view: static sidebar + form props (no API / logs row data). */
const RECON_VIEW_DUMMY_AS_AT = '2024-12-31';
const RECON_VIEW_DUMMY_CURRENCY = 'GBP';
const RECON_VIEW_DUMMY_MAPPING_ID = '__recon_view_dummy__';
const RECON_VIEW_DUMMY_SIDEBAR_GENERATED = '15/03/2026 10:30:00';
const RECON_VIEW_DUMMY_SIDEBAR_AS_AT = '31/12/2024';

import GenTaxImage from 'assets/images/gen-tax-img-cropped.svg';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import { EntityNarrowSidePanelChrome } from './entity-narrow-side-panel-chrome';
import { TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH } from './tax-rates-promotion-banner';

const ENTITY_DETAILS_BREAKPOINT = 1500;
const ENTITY_DETAILS_HEIGHT_BREAKPOINT = 750;
const PAGE_SIZE_RECONCILIATION_LOGS = 'page-size-reconciliation-logs';
/** Wide + narrow form sidebar — slightly wider than legacy 345px so help text wraps less. */
const RECONCILIATION_FORM_SIDEBAR_WIDTH_PX = 380;

/** One line of “Typical issues” body text — icon vertically centers to first line. */
const RECON_TYPICAL_ISSUE_ICON_WRAP: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  minHeight: 'calc(12px * 1.55)',
};

type PublishedReconciliationReportRow = {
  key: string;
  generatedBy: string;
  generatedDate: string;
  asAtDate: string;
  gtBalance: number | null;
  providerBalance: number | null;
  reconciledValue: number;
  status: string;
  /** Same role as live `reconciliationRun.mappingId` — from logs API when present. */
  mappingId?: string;
  reportId?: string;
  preparerUserId?: string;
  preparerFirstName?: string;
  preparerLastName?: string;
  preparerProfilePicture?: string;
};

interface ReconciliationTabProps {
  organisation?: any;
  isActive?: boolean;
  menuSize: MenuSize;
  selectedMaps?: import('store/organisation/models').Mapping[];
  loadCustomersAndSuppliers?: (connectionId: string) => void;
  activateVatChecker?: (
    connectionId: string,
    orgName: string,
    uuid: string
  ) => void;
  openReconciliationDrawer?: () => void;
  onAddReturn?: () => void;
  currentUser?: User;
  hasAdvancedUserRights?: boolean;
  hasNoTransactions?: boolean;
  reconciliation?: Map<string, string>;
  reconciliationRun?: ReconciliationRun | null;
  vatReturnLogs?: import('store/vat/models').VatReturnLog[];
  handlePreferences?: (
    action: import('utils/preferences').PreferenceActions,
    preference: import('utils/preferences').Preference
  ) => any;
  /** From `getViewReconciliationData` (organisation details); wiring TBD. */
  viewReconciliationData?: ViewReconciliationDataResponse;
}

export const ReconciliationTab: React.FC<ReconciliationTabProps> = ({
  organisation,
  isActive = false,
  menuSize: _menuSize,
  selectedMaps,
  loadCustomersAndSuppliers,
  activateVatChecker,
  openReconciliationDrawer,
  onAddReturn,
  currentUser,
  hasAdvancedUserRights,
  hasNoTransactions,
  reconciliation,
  reconciliationRun,
  vatReturnLogs,
  handlePreferences,
  viewReconciliationData,
}) => {
  const { t, i18n } = useTranslation();

  const renderBoldPrefixBeforeColon = (text: string) => {
    const colonIndex = text.indexOf(':');
    if (colonIndex === -1) return text;
    const prefix = text.slice(0, colonIndex);
    const suffix = text.slice(colonIndex + 1).trimStart();

    return (
      <>
        <strong>{prefix}</strong>: {suffix}
      </>
    );
  };
  const dispatch = useDispatch();
  const navigate = useNavigate();
  /** Always holds the latest value of hasUnsavedReconciliation so the history.block
   *  callback can read it without being recreated every time that flag changes. */
  const hasUnsavedReconciliationRef = React.useRef(false);
  const vatLoading = useSelector(getVatLoading);
  const reconciliationGenerating = useSelector(getReconciliationGenerating);
  const reconciliationLoading = useSelector(getReconciliationLoading);
  const reconciliationReportId = useSelector(getReconciliationReportId);
  const totalTaxTransactions = useSelector(
    getReconciliationTotalTaxTransactions
  );
  const totalTaxExceptions = useSelector(getReconciliationTotalTaxExceptions);
  const totalTaxOthers = useSelector(getReconciliationTotalTaxOthers);
  const transactionsCount = useSelector(getReconciliationTransactionsSize);
  const exceptionsCount = useSelector(getReconciliationExceptionsSize);
  const othersCount = useSelector(getReconciliationOthersSize);
  const reconciliationLogs = useSelector(getReconciliationLogs);
  const mappedReturns = useSelector(getOrgMappedReturns);
  const saveReportLoading = useSelector(getSaveReconciliationReportLoading);
  const saveReportSuccess = useSelector(getSaveReconciliationReportSuccess);
  const logsTotalElements = useSelector(getReconciliationLogsTotalElements);
  const handleReconciliationPreferences = React.useCallback(
    (
      _section: import('utils/preferences').PreferenceSections,
      action: import('utils/preferences').PreferenceActions,
      preference: import('utils/preferences').Preference
    ) => handlePreferences?.(action, preference),
    [handlePreferences]
  );
  const { formatWithParens } = useNumberFormat();
  const reconTotalRef = React.useRef<ReconTotal | null>(null);
  const handleReconTotalSnapshot = React.useCallback(
    (reconTotal: ReconTotal) => {
      reconTotalRef.current = reconTotal;
    },
    []
  );
  const [editNote, setEditNote] = React.useState(false);
  const [noteText, setNoteText] = React.useState<string>('');
  const [viewingSavedLogId, setViewingSavedLogId] = React.useState<
    string | null
  >(null);
  const [viewingSavedMappingId, setViewingSavedMappingId] = React.useState<
    string | null
  >(null);
  const isViewingSavedLog = viewingSavedLogId !== null;
  const fallbackSavedViewMappingId = React.useMemo(
    () => selectedMaps?.[0]?.mappingId || '',
    [selectedMaps?.[0]?.mappingId]
  );
  const lastViewReportRequestKeyRef = React.useRef<string>('');

  const savedViewReconTotals = React.useMemo(():
    | Pick<
        ReconTotal,
        | 'gtBalance'
        | 'xeroBalance'
        | 'difference'
        | 'totalTaxBalance'
        | 'account'
        | 'openTransactionsCount'
        | 'openExceptionsCount'
        | 'totalTaxOpenTransactions'
        | 'totalTaxOpenExceptions'
        | 'savedStatus'
        | 'openDataLogId'
      >
    | undefined => {
    if (!isViewingSavedLog || !viewReconciliationData?.reconTotal) {
      return undefined;
    }
    const r = viewReconciliationData.reconTotal;
    return {
      gtBalance: r.gtBalance,
      xeroBalance: r.xeroBalance,
      difference: r.difference,
      totalTaxBalance: r.totalTaxBalance,
      account: r.account,
      openDataLogId: r.openDataLogId,
      openTransactionsCount: r.openTransactionsCount,
      openExceptionsCount: r.openExceptionsCount,
      totalTaxOpenTransactions: r.totalTaxOpenTransactions,
      totalTaxOpenExceptions: r.totalTaxOpenExceptions,
      savedStatus: r.savedStatus ?? null,
    };
  }, [isViewingSavedLog, viewReconciliationData]);

  const savedViewToReportingDate = React.useMemo(
    () => viewReconciliationData?.dataLog?.toReportingDate ?? null,
    [viewReconciliationData]
  );

  const savedViewMappedReturnHit = React.useMemo(():
    | MappedReturn
    | undefined => {
    if (!isViewingSavedLog || !organisation?.uuid || !mappedReturns?.length) {
      return undefined;
    }
    return findSavedViewMappedReturnHit({
      organisationId: organisation.uuid,
      mappedReturns,
      dataLog: viewReconciliationData?.dataLog,
      openDataLogId:
        viewReconciliationData?.reconTotal?.openDataLogId?.trim() || '',
      selectedMaps,
    });
  }, [
    isViewingSavedLog,
    organisation?.uuid,
    mappedReturns,
    viewReconciliationData?.dataLog,
    viewReconciliationData?.reconTotal?.openDataLogId,
    selectedMaps,
  ]);

  const savedViewMappingIdFromMappedReturns =
    savedViewMappedReturnHit?.mappingId;

  const savedViewOpenPeriodFromMappedReturns = React.useMemo(
    (): SavedViewOpenPeriodFromMappedReturns | undefined =>
      buildSavedViewOpenPeriodFromHit(savedViewMappedReturnHit, selectedMaps),
    [savedViewMappedReturnHit, selectedMaps]
  );

  const activeReconciliationMappingId = React.useMemo(() => {
    if (isViewingSavedLog) {
      return (
        viewingSavedMappingId ||
        viewReconciliationData?.dataLog?.mappingId ||
        savedViewMappingIdFromMappedReturns ||
        ''
      );
    }
    return reconciliationRun?.mappingId || '';
  }, [
    isViewingSavedLog,
    viewingSavedMappingId,
    viewReconciliationData?.dataLog?.mappingId,
    savedViewMappingIdFromMappedReturns,
    reconciliationRun?.mappingId,
  ]);

  const activeReconciliationMapping = React.useMemo(
    () =>
      selectedMaps?.find((m) => m.mappingId === activeReconciliationMappingId),
    [selectedMaps, activeReconciliationMappingId]
  );

  const sidebarCurrency = React.useMemo(() => {
    if (isViewingSavedLog) {
      return (
        viewReconciliationData?.dataLog?.currency?.trim() ||
        organisation?.currency?.trim() ||
        ''
      );
    }
    return (
      reconciliationRun?.currency?.trim() ||
      organisation?.currency?.trim() ||
      ''
    );
  }, [
    isViewingSavedLog,
    viewReconciliationData?.dataLog?.currency,
    reconciliationRun?.currency,
    organisation?.currency,
  ]);

  const sidebarContextLine = React.useMemo(() => {
    const reportLabel = t(
      'organisation.reconciliation-tax-control-title',
      'Tax Control Account Reconciliation'
    );
    const mappingName = activeReconciliationMapping?.mappingName?.trim();
    const cur = sidebarCurrency;
    if (mappingName && cur) return `${mappingName} (${cur})`;
    if (mappingName) return mappingName;
    if (cur) return `${reportLabel} (${cur})`;
    return reportLabel;
  }, [t, activeReconciliationMapping, sidebarCurrency]);

  const sidebarPeriodLine = React.useMemo(() => {
    const fmt = (d: string | null | undefined) =>
      d != null && String(d).trim() !== ''
        ? formatDisplayDate(d, 'DD/MM/YYYY', i18n.language)
        : '';

    if (isViewingSavedLog) {
      const fromRaw =
        savedViewOpenPeriodFromMappedReturns?.fromReportingDate ||
        viewReconciliationData?.dataLog?.fromReportingDate;
      const toRaw =
        savedViewOpenPeriodFromMappedReturns?.toReportingDate ||
        viewReconciliationData?.dataLog?.toReportingDate ||
        savedViewToReportingDate;
      const from = fmt(fromRaw);
      const to = fmt(toRaw ?? fromRaw);
      if (from && to) {
        return t('organisation.reconciliation-sidebar-period', {
          from,
          to: to || from,
        });
      }
      const asAt = fmt(savedViewToReportingDate ?? undefined);
      if (asAt) {
        return `${t('organisation.reconciliation-sidebar-as-at-label')} ${asAt}`;
      }
      return '';
    }

    const mappingId = reconciliationRun?.mappingId;
    const latestForMapping = (vatReturnLogs || [])
      .filter((log) => log.mappingId === mappingId)
      .sort((a, b) => {
        const da = a.generatedDate ? dayjs(a.generatedDate).valueOf() : 0;
        const db = b.generatedDate ? dayjs(b.generatedDate).valueOf() : 0;
        return db - da;
      })[0];

    const rawFrom =
      latestForMapping?.fromReportingDate || reconciliationRun?.asAtDate;
    const rawTo =
      reconciliationRun?.asAtDate ||
      latestForMapping?.toReportingDate ||
      rawFrom;
    const from = fmt(rawFrom);
    const to = fmt(rawTo);
    if (from && to) {
      return t('organisation.reconciliation-sidebar-period', {
        from,
        to: to || from,
      });
    }
    if (from) {
      return `${t('organisation.reconciliation-sidebar-as-at-label')} ${from}`;
    }
    return '';
  }, [
    i18n.language,
    isViewingSavedLog,
    savedViewOpenPeriodFromMappedReturns,
    viewReconciliationData?.dataLog?.fromReportingDate,
    viewReconciliationData?.dataLog?.toReportingDate,
    savedViewToReportingDate,
    reconciliationRun?.mappingId,
    reconciliationRun?.asAtDate,
    vatReturnLogs,
    t,
  ]);

  const [logsFiltersState, setLogsFiltersState] = React.useState<
    Record<string, any> | undefined
  >(undefined);
  const publishedReconciliationReports: PublishedReconciliationReportRow[] =
    React.useMemo(
      () =>
        (reconciliationLogs || []).map((log) => ({
          key: log.uuid,
          generatedBy:
            log.preparerUser?.fullName ||
            [log.preparerUser?.firstName, log.preparerUser?.lastName]
              .filter(Boolean)
              .join(' ') ||
            log.preparerUser?.email ||
            '-',
          generatedDate: log.generatedDate,
          asAtDate: log.toDate,
          gtBalance: log.gtBalance,
          providerBalance: log.xeroBalance,
          reconciledValue: Number(log.difference ?? 0),
          status: DataLogStatus.SAVED,
          reportId: log.uuid,
          mappingId: log.mappingId,
          preparerUserId: log.preparerUser?.userUuid,
          preparerFirstName: log.preparerUser?.firstName,
          preparerLastName: log.preparerUser?.lastName,
          preparerProfilePicture: log.preparerUser?.profilePicture,
        })),
      [reconciliationLogs]
    );
  const filteredPublishedReconciliationReports = React.useMemo(() => {
    const rows = publishedReconciliationReports || [];
    const generatedDateFilter = logsFiltersState?.generatedDate?.value;
    if (
      !Array.isArray(generatedDateFilter) ||
      generatedDateFilter.length !== 2 ||
      !generatedDateFilter[0] ||
      !generatedDateFilter[1]
    ) {
      return rows;
    }
    const from = dayjs(String(generatedDateFilter[0]), [
      'MM/DD/YYYY',
      'DD/MM/YYYY',
    ]).startOf('day');
    const to = dayjs(String(generatedDateFilter[1]), [
      'MM/DD/YYYY',
      'DD/MM/YYYY',
    ]).endOf('day');
    if (!from.isValid() || !to.isValid()) return rows;
    return rows.filter((row) => {
      const d = row.generatedDate ? dayjs(row.generatedDate) : null;
      return Boolean(
        d &&
          d.isValid() &&
          (d.isAfter(from) || d.isSame(from)) &&
          (d.isBefore(to) || d.isSame(to))
      );
    });
  }, [publishedReconciliationReports, logsFiltersState]);
  const logsPageSizePreferenceName = organisation?.uuid
    ? `${PAGE_SIZE_RECONCILIATION_LOGS}-${organisation.uuid}`
    : undefined;
  const preferredLogsPageSizeRaw =
    logsPageSizePreferenceName && handlePreferences
      ? handlePreferences(PreferenceActions.GET, {
          name: logsPageSizePreferenceName,
        })
      : undefined;
  const preferredLogsPageSize =
    typeof preferredLogsPageSizeRaw === 'number' && preferredLogsPageSizeRaw > 0
      ? preferredLogsPageSizeRaw
      : 10;

  const hasNoMappings = !selectedMaps || selectedMaps.length === 0;
  const isLoadingCurrentReconciliation =
    reconciliationGenerating ||
    (!!reconciliationRun &&
      reconciliationRun.connectionId === organisation?.connectionId &&
      vatLoading &&
      !reconciliation) ||
    (isViewingSavedLog && reconciliationLoading);
  const hasUnsavedReconciliation =
    reconciliationGenerating ||
    (!!reconciliationRun &&
      reconciliationRun.connectionId === organisation?.connectionId);
  // Keep ref in sync so the history.block callback always sees the latest value
  // without the useEffect needing to re-register itself on every change.
  hasUnsavedReconciliationRef.current = hasUnsavedReconciliation;
  const hasLiveRun =
    !!reconciliationRun &&
    reconciliationRun.connectionId === organisation?.connectionId;
  const showFormPanel = hasLiveRun || isViewingSavedLog;
  // Keep the "Get Started" banner hidden for the full reconciliation session,
  // even if intermediate chained calls briefly toggle store loading flags.
  const [reconciliationSessionStarted, setReconciliationSessionStarted] =
    React.useState(false);

  /** List + optional intro panel (not shown while the main reconciliation form is open). */
  const showReconciliationLandingRow =
    !hasNoMappings && !showFormPanel && !isLoadingCurrentReconciliation;
  /** Hide CTA during a session and in view-only entities (only used on landing layout). */
  const showReconcileIntroductionPanel =
    !reconciliationSessionStarted &&
    (!reconciliationRun ||
      reconciliationRun.connectionId !== organisation?.connectionId) &&
    organisation?.status !== EntityStatus.VIEW_ONLY;

  const { width: windowWidth, height: windowHeight } = useWindowDimensions();
  const reconciliationWideLayout =
    windowWidth > ENTITY_DETAILS_BREAKPOINT &&
    windowHeight > ENTITY_DETAILS_HEIGHT_BREAKPOINT;
  const [showReconIntroNarrowPanel, setShowReconIntroNarrowPanel] =
    React.useState(false);
  const [showReconFormSidebarNarrowPanel, setShowReconFormSidebarNarrowPanel] =
    React.useState(false);
  React.useEffect(() => {
    if (reconciliationWideLayout) {
      setShowReconIntroNarrowPanel(false);
      setShowReconFormSidebarNarrowPanel(false);
    }
  }, [reconciliationWideLayout]);

  React.useEffect(() => {
    if (!isActive) {
      setShowReconIntroNarrowPanel(false);
      setShowReconFormSidebarNarrowPanel(false);
    }
  }, [isActive]);

  const reconciliationNarrowMainColumnStyle: React.CSSProperties = {
    width: '100%',
    minWidth: 850,
    marginRight: '24px',
  };

  const resetReconciliationState = React.useCallback(() => {
    dispatch(resetReconciliation());
    dispatch(loadReconFailure());
    dispatch(clearReconciliationRun());
    setReconciliationSessionStarted(false);
    setViewingSavedLogId(null);
    setViewingSavedMappingId(null);
    setEditNote(false);
    setNoteText('');
    setLogsFiltersState(undefined);
    reconTotalRef.current = null;
  }, [dispatch]);

  React.useEffect(() => {
    // Always default this tab to the list/get-started view on entry.
    resetReconciliationState();
  }, [resetReconciliationState]);

  React.useEffect(() => {
    // Mark session as started whenever current entity has an active run/generation.
    if (
      reconciliationGenerating ||
      (reconciliationRun &&
        reconciliationRun.connectionId === organisation?.connectionId)
    ) {
      setReconciliationSessionStarted(true);
    }
  }, [reconciliationGenerating, reconciliationRun, organisation?.connectionId]);

  const prevReconciliationGeneratingRef = React.useRef(
    reconciliationGenerating
  );
  React.useEffect(() => {
    const wasGenerating = prevReconciliationGeneratingRef.current;
    prevReconciliationGeneratingRef.current = reconciliationGenerating;

    if (wasGenerating && !reconciliationGenerating) {
      const hasLiveRun =
        !!reconciliationRun &&
        reconciliationRun.connectionId === organisation?.connectionId;
      if (!hasLiveRun) {
        setReconciliationSessionStarted(false);
      }
    }
  }, [reconciliationGenerating, reconciliationRun, organisation?.connectionId]);

  const prevReconciliationReportIdRef = React.useRef<string | undefined>(
    undefined
  );
  React.useEffect(() => {
    // Successful publish dispatches resetReconciliation() (clears reportId) but does not
    // reset this local flag, so the banner stayed hidden while the main panel disappeared.
    const prev = prevReconciliationReportIdRef.current;
    const hadReport = Boolean(prev);
    if (hadReport && !reconciliationReportId && reconciliationSessionStarted) {
      setReconciliationSessionStarted(false);
    }
    prevReconciliationReportIdRef.current = reconciliationReportId;
  }, [reconciliationReportId, reconciliationSessionStarted]);

  React.useEffect(() => {
    // Reset all reconciliation UI/store state when switching entities.
    resetReconciliationState();
  }, [organisation?.connectionId, resetReconciliationState]);

  React.useEffect(() => {
    if (!organisation?.uuid) return;
    dispatch(
      fetchReconciliationLogsRequest(
        organisation.uuid,
        1,
        preferredLogsPageSize
      )
    );
  }, [dispatch, organisation?.uuid, preferredLogsPageSize]);

  // Fire view-reconciliation API when a saved row is selected (for inspection / testing).
  // UI stays on dummy data via viewSavedDummyMode; response still lands in Redux if you need it.
  React.useEffect(() => {
    if (!isViewingSavedLog || !viewingSavedLogId) {
      lastViewReportRequestKeyRef.current = '';
    }
  }, [isViewingSavedLog, viewingSavedLogId]);

  React.useEffect(() => {
    if (!isViewingSavedLog || !viewingSavedLogId || !organisation?.uuid) {
      return;
    }
    const mappingId = viewingSavedMappingId || fallbackSavedViewMappingId;
    const requestKey = [
      organisation.uuid,
      viewingSavedLogId,
      organisation.connectionId ?? organisation.uuid,
      mappingId,
      organisation?.currency ?? '',
    ].join('|');
    if (lastViewReportRequestKeyRef.current === requestKey) {
      return;
    }
    lastViewReportRequestKeyRef.current = requestKey;
    dispatch(
      viewReconciliationReportRequest({
        organisationId: organisation.uuid,
        dataLogId: viewingSavedLogId,
        connectionId: organisation.connectionId ?? organisation.uuid,
        mappingId,
        currency: organisation?.currency ?? '',
      })
    );
  }, [
    dispatch,
    isViewingSavedLog,
    viewingSavedLogId,
    organisation?.uuid,
    organisation?.connectionId,
    viewingSavedMappingId,
    fallbackSavedViewMappingId,
    organisation?.currency,
  ]);

  React.useEffect(() => {
    if (!saveReportSuccess || !organisation?.uuid) return;
    dispatch(
      fetchReconciliationLogsRequest(
        organisation.uuid,
        1,
        preferredLogsPageSize
      )
    );
  }, [dispatch, saveReportSuccess, organisation?.uuid, preferredLogsPageSize]);

  React.useEffect(() => {
    if (isActive || !isViewingSavedLog) return;
    // Leaving reconciliation while viewing a saved report should always
    // reopen in default/list view when user returns.
    setViewingSavedLogId(null);
    setViewingSavedMappingId(null);
    setReconciliationSessionStarted(false);
    setEditNote(false);
    setNoteText('');
  }, [isActive, isViewingSavedLog]);

  const blocker = useHistoryBlock((nextLocation) => {
    const currentPathname = window.location.pathname;
    if (nextLocation.pathname === currentPathname) return true;
    if (!hasUnsavedReconciliationRef.current) return true;
    return false;
  });

  React.useEffect(() => {
    if (blocker.state !== 'blocked') return;

    const continueWithoutSaving = () => {
      const nextLocation = blocker.location;
      dispatch(turnModalOff());
      resetReconciliationState();
      blocker.reset?.();
      if (nextLocation) {
        navigate({
          pathname: nextLocation.pathname,
          search: nextLocation.search || '',
        });
      }
    };

    const stayOnPage = () => {
      dispatch(turnModalOff());
      blocker.reset?.();
    };

    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('organisation.your-reconciliation-is-not-saved'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={stayOnPage}
          callback2={continueWithoutSaving}
          okText={t('organisation.stay')}
          okText2={t('vat-report.continue-without-saving')}
          showCancelBtn={false}
          description={t('organisation.reconciliation-unsaved-leave-description')}
        />
      ),
    };
    dispatch(turnModalOn(modalData));
  }, [blocker, dispatch, navigate, resetReconciliationState]);

  const onGetStartedClick = () => {
    if (hasNoMappings) return;

    const hasSubmittedSubmission = (vatReturnLogs || []).some((vrl) => {
      const raw = (vrl as any)?.status;
      const s = typeof raw === 'string' ? raw.trim() : '';
      return s === DataLogStatus.SUBMITTED || s.toLowerCase() === 'submitted';
    });

    if (!hasSubmittedSubmission) {
      const message: MessageData = {
        title: t('organisation.reconciliation-unavailable-title'),
        description: t('organisation.reconciliation-unavailable-description'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    openReconciliationDrawer?.();
  };

  const handleRecalculate = React.useCallback(
    (balanceBf: number) => {
      if (!organisation?.uuid) return;
      if (!reconciliationRun?.connectionId || !reconciliationRun?.asAtDate)
        return;

      // Persist balance b/f only; do not pass refreshRecon — that triggers loadReconRequest
      // and reloads the whole reconciliation report (poor UX).
      dispatch(
        updateOrganisationTaxBalanceRequest(organisation.uuid, balanceBf)
      );
    },
    [dispatch, organisation?.uuid, reconciliationRun]
  );

  /**
   * ID passed to `POST /auth/activity/add/note/{id}` and activity log fetch (same saga as VAT report).
   * 1. `viewReconciliationData.dataLog.uuid` — `VatReturnLog` from the view-reconciliation response.
   * 2. `viewingSavedLogId` — `log.uuid` from published-recon list / view URL until payload has `dataLog`.
   */
  const getSavedViewDataLogId = (): string | undefined => {
    if (!isViewingSavedLog) return undefined;
    const dataLogUuid = viewReconciliationData?.dataLog?.uuid?.trim() || '';
    if (dataLogUuid) return dataLogUuid;
    return viewingSavedLogId ?? undefined;
  };

  /** Always uses `getSavedViewDataLogId()` (dataLog.uuid / viewingSavedLogId), not the id from ActivityLogs props. */
  const updateNote = (noteText: string, _dataLogIdFromDrawer: string) => {
    const dataLogId = getSavedViewDataLogId();
    if (!dataLogId) {
      dispatch(
        turnMessageOn({
          title: t('organisation.reconciliation-unable-save-note-title'),
          description: t('organisation.reconciliation-data-not-available'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }
    if (!noteText || noteText === '') {
      const message: MessageData = {
        title: t('main.reconciliation-empty-note-title'),
        description: t('main.reconciliation-empty-note-desc'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
    } else {
      dispatch(addNoteRequest(noteText, dataLogId));
    }
  };

  const openActivityLogs = () => {
    if (!isViewingSavedLog) return;
    const dataLogId = getSavedViewDataLogId();
    if (!dataLogId) {
      const message: MessageData = {
        title: t('organisation.reconciliation-activity-unable-title'),
        description: t('organisation.reconciliation-data-not-available'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return;
    }

    const savedDl = viewReconciliationData?.dataLog;
    const connectionForActivity =
      savedDl?.applicationConnectionID ?? organisation?.connectionId ?? '';
    const mappingForActivity =
      savedDl?.mappingId ?? viewingSavedMappingId ?? '';

    const mockVatRunData: Partial<VatRunData> = {
      dataLogId,
      tokenId: connectionForActivity,
      mappingId: mappingForActivity,
      status: savedDl?.status,
    };

    const drawerData: DrawerData = {
      title: t('organisation.reconciliation-activity'),
      type: DrawerType.ACTIVITY_LOGS,
      component: (
        <ActivityLogs
          data={mockVatRunData as VatRunData}
          activityLogs={undefined}
          updateNote={updateNote}
          connectionId={connectionForActivity}
          mappingId={mappingForActivity}
        />
      ),
    };
    dispatch(turnDrawerOn(drawerData));
  };

  const onNoteChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(e.target.value);
  };

  const onNoteSave = () => {
    const dataLogId = getSavedViewDataLogId();
    if (!dataLogId) {
      const message: MessageData = {
        title: t('organisation.reconciliation-unable-save-note-title'),
        description: t('organisation.reconciliation-data-not-available'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return;
    }
    updateNote(noteText, dataLogId);
    if (noteText !== '') {
      setEditNote(false);
      setNoteText('');
    }
  };

  const handleAddNoteClick = () => {
    const dataLogId = getSavedViewDataLogId();
    if (!dataLogId) {
      const message: MessageData = {
        title: t('organisation.reconciliation-unable-add-note-title'),
        description: t('organisation.reconciliation-data-not-available'),
        type: MessageStates.WARNING,
      };
      dispatch(turnMessageOn(message));
      return;
    }
    setEditNote(!editNote);
  };

  const handleLogsPageChange = React.useCallback(
    (page: number, pageSize: number, filters?: object) => {
      const organisationId = organisation?.uuid;
      if (!organisationId) return;
      const typedFilters = (filters as Record<string, any>) || undefined;
      setLogsFiltersState(typedFilters);
      dispatch(
        fetchReconciliationLogsRequest(
          organisationId,
          page,
          pageSize,
          typedFilters
        )
      );
    },
    [dispatch, organisation?.uuid]
  );

  const saveLogsPageSize = React.useCallback(
    (pageSize: number) => {
      const organisationId = organisation?.uuid;
      if (!organisationId) return;
      handlePreferences?.(PreferenceActions.SET, {
        name: `${PAGE_SIZE_RECONCILIATION_LOGS}-${organisationId}`,
        value: pageSize,
      });
    },
    [handlePreferences, organisation?.uuid]
  );

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder === 'ascend') {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    if (sortOrder === 'descend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
    return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
  };

  const renderReconciliationSkeleton = () => (
    <div
      style={{
        paddingTop: 0,
        display: 'flex',
        justifyContent: 'space-between',
        width: '100%',
        gap: 40,
        alignContent: 'end',
      }}
    >
      <StyledCard
        style={{
          width: '100%',
          minWidth: 800,
          maxWidth: 1150,
          fontSize: 14,
          fontWeight: 400,
          padding: '24px',
        }}
      >
        <Skeleton.Input
          active
          style={{ width: 400, height: 32, marginBottom: 24 }}
        />

        <div
          style={{
            background: 'var(--color-surface-raised, #ffffff)',
            border: '1px solid #e3ebeb',
            borderRadius: 8,
            padding: 20,
            marginBottom: 48,
          }}
        >
          <Skeleton.Input
            active
            style={{ width: 250, height: 20, marginBottom: 20 }}
          />
          <div style={{ display: 'flex', gap: 130, marginBottom: 16 }}>
            <div style={{ flex: 1 }}>
              <Skeleton.Input
                active
                style={{ width: '100%', height: 16, marginBottom: 8 }}
              />
              <Skeleton.Input active style={{ width: 150, height: 24 }} />
            </div>
            <div style={{ flex: 1 }}>
              <Skeleton.Input
                active
                style={{ width: '100%', height: 16, marginBottom: 8 }}
              />
              <Skeleton.Input active style={{ width: 150, height: 24 }} />
            </div>
            <div style={{ flex: 1 }}>
              <Skeleton.Input
                active
                style={{ width: '100%', height: 16, marginBottom: 8 }}
              />
              <Skeleton.Input active style={{ width: 150, height: 24 }} />
            </div>
          </div>
        </div>

        <div
          style={{
            background: 'var(--color-surface-raised, #ffffff)',
            border: '1px solid var(--color-border, #dbeae3)',
            borderRadius: 8,
            padding: 20,
          }}
        >
          <Skeleton.Input
            active
            style={{ width: 200, height: 18, marginBottom: 20 }}
          />
          <Skeleton paragraph={{ rows: 8 }} active />
        </div>
      </StyledCard>

      <div
        style={{
          width: `${RECONCILIATION_FORM_SIDEBAR_WIDTH_PX}px`,
          flexShrink: 0,
          alignSelf: 'flex-start',
        }}
      >
        <Skeleton.Button
          active
          style={{ width: 100, height: 28, marginBottom: 20 }}
        />
        <Skeleton.Avatar active size={25} style={{ marginBottom: 10 }} />
        <Skeleton.Input
          active
          style={{ width: '100%', height: 20, marginBottom: 16 }}
        />
        <Skeleton paragraph={{ rows: 3 }} active />
        <div style={{ marginTop: 32, marginBottom: 32 }}>
          <Skeleton.Button active style={{ width: '100%', height: 40 }} />
        </div>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            marginBottom: 16,
          }}
        >
          <img
            src={ReconcileImage}
            alt={t('organisation.reconciliation-tab')}
            style={{
              maxWidth: '250px',
              width: '100%',
              height: 'auto',
              display: 'block',
            }}
          />
        </div>
      </div>
    </div>
  );

  const handlePublishReport = () => {
    const tokenId =
      reconciliationRun?.connectionId || organisation?.connectionId;
    const mappingId = reconciliationRun?.mappingId;
    const reportId = reconciliationReportId;
    const currentMapping = selectedMaps?.find((m) => m.mappingId === mappingId);
    const reportingTypeId = currentMapping?.returnTypeId;
    const latestForMapping = (vatReturnLogs || [])
      .filter((log) => log.mappingId === mappingId)
      .sort((a, b) => {
        const da = a.generatedDate ? dayjs(a.generatedDate).valueOf() : 0;
        const db = b.generatedDate ? dayjs(b.generatedDate).valueOf() : 0;
        return db - da;
      })[0];
    const rawFromDate =
      latestForMapping?.fromReportingDate || reconciliationRun?.asAtDate;
    const fromDate = rawFromDate
      ? dayjs(rawFromDate).format('YYYY-MM-DD')
      : undefined;
    const rawToDate =
      reconciliationRun?.asAtDate ||
      latestForMapping?.toReportingDate ||
      rawFromDate;
    const toDate = rawToDate
      ? dayjs(rawToDate).format('YYYY-MM-DD')
      : undefined;
    const currency = reconciliationRun?.currency || organisation?.currency;
    const generatedDate = dayjs().format('DD/MM/YYYY HH:mm:ss');

    if (
      !tokenId ||
      !mappingId ||
      !reportId ||
      !reportingTypeId ||
      !fromDate ||
      !toDate ||
      !currency
    ) {
      dispatch(
        turnMessageOn({
          title: t('organisation.reconciliation-unable-save-note-title'),
          description: t('organisation.reconciliation-data-not-available'),
          type: MessageStates.WARNING,
        })
      );
      return;
    }

    dispatch(
      saveReconciliationReportRequest({
        organisationId: tokenId,
        mappingId,
        fromDate,
        toDate,
        generatedDate,
        currency,
        reportingTypeId,
        reportId,
        jsonStringsRequest: {
          reconTotal: reconTotalRef.current ?? EMPTY_RECON_TOTAL,
          totalTaxTransactions: Number(totalTaxTransactions ?? 0),
          totalTaxExceptions: Number(totalTaxExceptions ?? 0),
          totalTaxOthers: Number(totalTaxOthers ?? 0),
          transactionsCount: Number(transactionsCount ?? 0),
          exceptionsCount: Number(exceptionsCount ?? 0),
          othersCount: Number(othersCount ?? 0),
        },
      })
    );
  };

  const openPublishConfirmation = () => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('organisation.reconciliation-publish-confirm-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={handlePublishReport}
          okText={t('organisation.reconciliation-publish-button')}
          description={t(
            'organisation.reconciliation-publish-confirm-description'
          )}
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const openCancelConfirmation = () => {
    const modalData: ModalData = {
      type: WindowType.WARNING,
      title: t('vat-report.cancel-report-title'),
      icon: <img src={GenTaxImage} alt="" />,
      component: (
        <ConfirmBoxContainer
          callback={resetReconciliationState}
          cancelText={t('organisation.reconciliation-cancel-confirm-stay')}
          okText={t('organisation.reconciliation-cancel-confirm-ok')}
          description={t(
            'organisation.reconciliation-cancel-confirm-description'
          )}
        />
      ),
    };

    dispatch(turnModalOn(modalData));
  };

  const hasPublishedReconciliationReports = (logsTotalElements ?? 0) > 0;

  /** Right-hand intro card on the landing layout (same width pattern as Tax Rates tab side panel). */
  const renderReconciliationIntroSidePanel = () => {
    const providerName =
      organisation?.providerName ||
      t('organisation.reconciliation-provider-placeholder');
    const primaryActionLabel = hasPublishedReconciliationReports
      ? t('organisation.reconciliation-new-report')
      : t('organisation.get-started');
    return (
      <StyledCard
        style={{
          width: TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH,
          height: 'fit-content',
        }}
      >
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            alignItems: 'center',
          }}
        >
          <div style={{ width: '100%', textAlign: 'left' }}>
            <CardTitleLargeSpan style={{ marginBottom: 10, display: 'block' }}>
              {t('organisation.reconciliation-banner-title')}
            </CardTitleLargeSpan>
            <CardDescSpan style={{ marginBottom: 16, display: 'block' }}>
              {t('organisation.reconciliation-banner-desc-1', {
                provider: providerName,
              })}
            </CardDescSpan>
            <CardDescSpan style={{ marginBottom: 0, display: 'block' }}>
              {t('organisation.reconciliation-banner-desc-2', {
                provider: providerName,
              })}
            </CardDescSpan>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
            }}
          >
            <FeatureDotBadge dotId="entities-reconciliation-get-started">
              <StyledTooltip
                placement="top"
                title={
                  hasNoMappings
                    ? t(
                        'organisation.reconciliation-create-mapping-first',
                        'You need to create a mapping first to be able to do a reconciliation.'
                      )
                    : ''
                }
              >
                <span style={{ display: 'inline-block' }}>
                  <ScopeButton
                    type="dark-orange"
                    width={
                      hasPublishedReconciliationReports ? '200px' : '180px'
                    }
                    height="40px"
                    fontSize="15px"
                    lineheight="12px"
                    onClick={onGetStartedClick}
                    disabled={hasNoMappings}
                  >
                    {primaryActionLabel}
                  </ScopeButton>
                </span>
              </StyledTooltip>
            </FeatureDotBadge>
          </div>
          <img
            alt={t('organisation.reconciliation-tab')}
            src={ReconcileImage}
            style={{
              width: 250,
              height: 'auto',
              display: 'block',
            }}
          />
        </div>
      </StyledCard>
    );
  };

  const renderReconciliationFormSidebarBody = () => (
    <>
      <div style={{ marginBottom: 20 }}>
        <StatusGetter
          status={
            isViewingSavedLog ? DataLogStatus.SAVED : DataLogStatus.NOT_SAVED
          }
        />
      </div>

      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          marginBottom: 10,
        }}
      >
        {organisation?.providerName && (
          <ProviderLogoNav
            providerName={organisation.providerName}
            shortCode={organisation.shortCode}
            organisationId={organisation.uuid}
            style={{
              cursor: 'pointer',
              display: 'flex',
              flexShrink: 0,
            }}
          >
            <ProviderIcon
              providerName={organisation.providerName}
              alt={organisation.providerName}
              width={25}
            />
          </ProviderLogoNav>
        )}
        <span
          style={{
            fontFamily: 'Inter',
            fontSize: 14,
            fontWeight: 600,
            lineHeight: '20px',
            color: 'var(--color-text-dark, #042a2a)',
          }}
        >
          {organisation?.friendlyName ||
            organisation?.legalName ||
            t('organisation.reconciliation-company-placeholder')}
        </span>
      </div>

      <div
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          fontWeight: 400,
          lineHeight: '18px',
          color: '#4a5d5d',
          marginBottom: 16,
        }}
      >
        <div style={{ marginBottom: 2 }}>{sidebarContextLine}</div>
        <div style={{ marginBottom: 2 }}>{sidebarPeriodLine || '\u2014'}</div>
        <div>
          {t('organisation.reconciliation-sidebar-generated', {
            datetime: isViewingSavedLog
              ? viewReconciliationData?.dataLog?.generatedDate
                ? dayjs(viewReconciliationData.dataLog.generatedDate).format(
                    'DD/MM/YYYY HH:mm:ss'
                  )
                : RECON_VIEW_DUMMY_SIDEBAR_GENERATED
              : dayjs().format('DD/MM/YYYY HH:mm:ss'),
          })}
        </div>
      </div>

      <div
        style={{
          borderBottom: '1px solid var(--color-border, #dbeae3)',
          marginBottom: 20,
        }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '32px 0',
          gap: 8,
        }}
      >
        {isViewingSavedLog ? (
          <ScopeButton
            type="default"
            width="100%"
            height="40px"
            style={{
              fontSize: 14,
              fontWeight: 600,
              background: 'white',
            }}
            onClick={() => {
              setViewingSavedLogId(null);
              setReconciliationSessionStarted(false);
            }}
          >
            {t('common.close')}
          </ScopeButton>
        ) : (
          <>
            <ScopeButton
              type="primary"
              width="388px"
              height="40px"
              style={{ fontSize: 14, fontWeight: 600 }}
              onClick={openPublishConfirmation}
              loading={saveReportLoading}
              disabled={saveReportLoading}
            >
              {t('organisation.reconciliation-publish-button')}
            </ScopeButton>
            <ScopeButton
              type="default"
              width="188px"
              height="40px"
              style={{
                fontSize: 14,
                fontWeight: 600,
                background: 'white',
              }}
              onClick={openCancelConfirmation}
              disabled={saveReportLoading}
            >
              {t('common.cancel')}
            </ScopeButton>
          </>
        )}
      </div>
      {isViewingSavedLog && (
        <>
          <div
            style={{
              marginBottom: 10,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Space size={8}>
              <ExportButton
                icon={<IconNote style={{ width: 20, height: 20 }} />}
                onClick={handleAddNoteClick}
                style={{ marginBottom: 0 }}
              >
                {!editNote
                  ? t('common.add-note')
                  : t('organisation.reconciliation-close-note')}
              </ExportButton>
              <ExportButton
                icon={
                  <IconBell
                    style={{ width: 20, height: 20, color: 'var(--color-text-muted, #5a6a6a)' }}
                  />
                }
                onClick={openActivityLogs}
                style={{ marginBottom: 0 }}
              >
                {t('organisation.reconciliation-activity')}
              </ExportButton>
            </Space>
          </div>

          {editNote && (
            <>
              <TextArea rows={4} onChange={onNoteChange} value={noteText} />
              <div style={{ marginBottom: 20 }} />
              <ScopeButton type={'primary'} onClick={onNoteSave} width="100%">
                {t('organisation.reconciliation-save-note')}
              </ScopeButton>
              <div style={{ marginBottom: 20 }} />
            </>
          )}
        </>
      )}

      <div
        style={{
          marginBottom: 20,
          marginTop: 20,
          borderBottom: '1px solid var(--color-border, #dbeae3)',
        }}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginBottom: 16,
        }}
      >
        <img
          src={ReconcileImage}
          alt={t('organisation.reconciliation-tab')}
          style={{
            maxWidth: '250px',
            width: '100%',
            height: 'auto',
            display: 'block',
          }}
        />
      </div>
      <div
        style={{
          fontFamily: 'Inter',
          fontSize: 13,
          fontWeight: 600,
          lineHeight: '18px',
          color: 'var(--color-text-dark, #042a2a)',
          marginBottom: 10,
        }}
      >
        {t('organisation.reconciliation-typical-issues-heading')}
      </div>
      <div
        style={{
          fontFamily: 'Inter',
          fontSize: 12,
          fontWeight: 400,
          lineHeight: 1.55,
          color: 'var(--color-text-muted, #5a6a6a)',
        }}
      >
        <div
          style={{
            marginBottom: 10,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={RECON_TYPICAL_ISSUE_ICON_WRAP}>
            <QuestionMarkTooltip
              iconSize={12}
              title={t('organisation.reconciliation-issue-unposted', {
                provider:
                  organisation?.providerName ||
                  t('organisation.reconciliation-provider-placeholder'),
              })}
            />
          </span>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            {renderBoldPrefixBeforeColon(
              t('organisation.reconciliation-issue-unposted', {
                provider:
                  organisation?.providerName ||
                  t('organisation.reconciliation-provider-placeholder'),
              })
            )}
          </span>
        </div>
        <div
          style={{
            marginBottom: 10,
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={RECON_TYPICAL_ISSUE_ICON_WRAP}>
            <QuestionMarkTooltip
              iconSize={12}
              title={t('organisation.reconciliation-issue-unresolved')}
            />
          </span>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            {renderBoldPrefixBeforeColon(
              t('organisation.reconciliation-issue-unresolved')
            )}
          </span>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 8,
            alignItems: 'flex-start',
          }}
        >
          <span style={RECON_TYPICAL_ISSUE_ICON_WRAP}>
            <QuestionMarkTooltip
              iconSize={12}
              title={t('organisation.reconciliation-issue-mapping')}
            />
          </span>
          <span style={{ flex: 1, minWidth: 0, textAlign: 'left' }}>
            {renderBoldPrefixBeforeColon(
              t('organisation.reconciliation-issue-mapping')
            )}
          </span>
        </div>
      </div>
    </>
  );

  const renderReconciliationFormMainPanel = () => (
    <StyledCard
      style={{
        width: '100%',
        minWidth: reconciliationWideLayout ? 800 : 0,
        maxWidth: 1150,
        fontSize: 14,
        fontWeight: 400,
        padding: '24px',
      }}
    >
      <VatControlAccountReconciliation
        organisation={organisation}
        reconciliation={isViewingSavedLog ? undefined : reconciliation}
        asAtDate={
          isViewingSavedLog
            ? savedViewToReportingDate ?? RECON_VIEW_DUMMY_AS_AT
            : reconciliationRun?.asAtDate
        }
        currency={
          isViewingSavedLog
            ? viewReconciliationData?.dataLog?.currency ??
              organisation?.currency ??
              RECON_VIEW_DUMMY_CURRENCY
            : reconciliationRun?.currency ?? organisation?.currency
        }
        mappingId={
          isViewingSavedLog
            ? RECON_VIEW_DUMMY_MAPPING_ID
            : reconciliationRun?.mappingId
        }
        selectedMaps={selectedMaps}
        vatReturnLogs={isViewingSavedLog ? [] : vatReturnLogs}
        onChangeDate={isViewingSavedLog ? undefined : openReconciliationDrawer}
        onRecalculate={isViewingSavedLog ? undefined : handleRecalculate}
        onReconTotalSnapshot={
          isViewingSavedLog ? undefined : handleReconTotalSnapshot
        }
        handlePreferences={handleReconciliationPreferences}
        viewSavedDummyMode={isViewingSavedLog}
        savedViewReconTotals={savedViewReconTotals}
        savedViewTaxReportTypeId={
          viewReconciliationData?.dataLog?.taxReportTypeId
        }
        savedViewMappingId={
          viewingSavedMappingId ||
          viewReconciliationData?.dataLog?.mappingId ||
          savedViewMappingIdFromMappedReturns
        }
        savedViewDataLog={
          isViewingSavedLog ? viewReconciliationData?.dataLog : undefined
        }
        savedViewFetchedLogId={
          isViewingSavedLog ? viewingSavedLogId : undefined
        }
        savedViewOpenPeriodFromMappedReturns={
          isViewingSavedLog ? savedViewOpenPeriodFromMappedReturns : undefined
        }
      />
    </StyledCard>
  );

  return (
    <div
      className="entity-reconciliation-tab"
      style={{ minHeight: 'calc(100vh - 120px)' }}
    >
      <div
        className={`reconciliation-tab-wrapper${
          !reconciliationWideLayout ? ' reconciliation-tab-wrapper--narrow' : ''
        }`}
      >
        {/* Empty state when no mappings - same as overview */}
        {hasNoMappings && (
          <div
            style={{
              paddingTop: 0,
              display: 'flex',
              justifyContent: 'center',
              maxWidth: 1170,
              width: '100%',
              margin: '0 auto',
            }}
          >
            <StyledCard
              style={{
                border: '1px dashed var(--color-primary, #41aaaf)',
                boxShadow: '0px 4px 10px 0px #0000000D',
                margin: '0 0 20px',
                display: 'flex',
                justifyContent: 'center',
                padding: '94px',
                width: '100%',
                overflow: 'auto',
              }}
            >
              <Space direction="vertical" align="center">
                <CardTitleLargeSpan style={{ fontSize: 24 }}>
                  {t('organisation.empty-tax-return')}
                </CardTitleLargeSpan>
                <CardTitleSpan style={{ fontSize: 16, fontWeight: 300 }}>
                  {t('organisation.empty-tax-returns-action')}
                </CardTitleSpan>
                <StyledTooltip
                  placement="top"
                  title={
                    hasNoTransactions
                      ? t('organisation.upload-transactions-before-add-return')
                      : hasAdvancedUserRights &&
                        !hasAdvancedUserRight(
                          currentUser,
                          organisation as any,
                          AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                        )
                      ? t('global.edit-return-rights-warning')
                      : ''
                  }
                >
                  <ScopeButton
                    type="primary"
                    height="40px"
                    width="197px"
                    style={{ marginTop: '30px' }}
                    onClick={onAddReturn}
                    disabled={
                      hasNoTransactions ||
                      !organisation?.connectedProperly ||
                      (currentUser &&
                        AccountType[currentUser.accountType] ===
                          AccountType.CLIENT) ||
                      (hasAdvancedUserRights &&
                        !hasAdvancedUserRight(
                          currentUser,
                          organisation as any,
                          AdvancedUserRightsCodes.CREATE_EDIT_RETURN
                        ))
                    }
                  >
                    {t('organisation.add-tax-return')}
                  </ScopeButton>
                </StyledTooltip>
              </Space>
            </StyledCard>
          </div>
        )}

        {isLoadingCurrentReconciliation && renderReconciliationSkeleton()}

        {/* VAT Control Account Reconciliation - show when live run exists OR viewing a saved log */}
        {!isLoadingCurrentReconciliation &&
          showFormPanel &&
          (reconciliationWideLayout ? (
            <div
              style={{
                paddingTop: 0,
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 40,
                alignContent: 'end',
              }}
            >
              {renderReconciliationFormMainPanel()}
              <div
                style={{
                  width: `${RECONCILIATION_FORM_SIDEBAR_WIDTH_PX}px`,
                  flexShrink: 0,
                  alignSelf: 'flex-start',
                  maxHeight: 'fit-content',
                }}
              >
                {renderReconciliationFormSidebarBody()}
              </div>
            </div>
          ) : (
            <div className="entity-overview-tab entity-details-narrow-side-shell">
              <div style={reconciliationNarrowMainColumnStyle}>
                <div
                  style={{
                    padding: '0 40px',
                    width: '100%',
                    maxWidth: 1250,
                    margin: '0 auto',
                  }}
                >
                  {renderReconciliationFormMainPanel()}
                </div>
              </div>
              <EntityNarrowSidePanelChrome
                panelOpen={showReconFormSidebarNarrowPanel}
                onOpenPanel={() => setShowReconFormSidebarNarrowPanel(true)}
                onClosePanel={() => setShowReconFormSidebarNarrowPanel(false)}
                tabLabel={t('organisation.reconciliation-form-sidebar-tab')}
                panelWidthPx={RECONCILIATION_FORM_SIDEBAR_WIDTH_PX}
              >
                <div
                  style={{
                    width: '100%',
                    boxSizing: 'border-box',
                    padding: 0,
                  }}
                >
                  {renderReconciliationFormSidebarBody()}
                </div>
              </EntityNarrowSidePanelChrome>
            </div>
          ))}

        {/* Published reports (always on landing layout); ScopeTable empty state when there are none */}
        {showReconciliationLandingRow &&
          (!reconciliationWideLayout && showReconcileIntroductionPanel ? (
            <div className="entity-overview-tab entity-details-narrow-side-shell">
              <div style={reconciliationNarrowMainColumnStyle}>
                <div
                  style={{
                    padding: '0 40px',
                    width: '100%',
                    maxWidth: 1250,
                    margin: '0 auto',
                    paddingTop: 8,
                  }}
                >
                  <StyledCard
                    style={{
                      width: '100%',
                      minWidth: 0,
                      flex: 1,
                      overflow: 'auto',
                      border: '1px solid #e3ebeb',
                      boxShadow: '0px 4px 10px 0px #0000000D',
                    }}
                  >
                    <div
                      style={{
                        padding: '5px',
                        paddingLeft: '5px',
                        paddingTop: '5px',
                        minWidth: 0,
                        margin: 'auto',
                      }}
                    >
                      <ScopeTable
                        className="reconciliation-reports-table"
                        // Keep filter wiring in place, but hide filter UI until BE filtering is ready.
                        columns={[
                          {
                            title: t('organisation.generated-by'),
                            dataIndex: 'generatedBy',
                            key: 'generatedBy',
                            width: 80,
                            className: 'recon-generated-by-col',
                            onHeaderCell: () => ({
                              className: 'recon-generated-by-col',
                            }),
                            onCell: () => ({
                              className: 'recon-generated-by-col',
                            }),
                            align: 'center',
                            ellipsis: true,
                            sorter: (
                              a: PublishedReconciliationReportRow,
                              b: PublishedReconciliationReportRow
                            ) =>
                              (a.generatedBy || '').localeCompare(
                                b.generatedBy || '',
                                undefined,
                                { sensitivity: 'base' }
                              ),
                            sortIcon: renderSortIcon,
                            render: (
                              _val: string,
                              record: PublishedReconciliationReportRow
                            ) => (
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                }}
                              >
                                {renderAvatar(
                                  record.preparerFirstName || '',
                                  record.preparerLastName || '',
                                  AvatarSizes.SMALL,
                                  record.preparerUserId || '',
                                  record.preparerProfilePicture
                                )}
                              </div>
                            ),
                          },
                          {
                            title: t('organisation.generated-date'),
                            dataIndex: 'generatedDate',
                            key: 'generatedDate',
                            width: 150,
                            sorter: (
                              a: (typeof publishedReconciliationReports)[0],
                              b: (typeof publishedReconciliationReports)[0]
                            ) => {
                              const da = a.generatedDate
                                ? dayjs(a.generatedDate).valueOf()
                                : 0;
                              const db = b.generatedDate
                                ? dayjs(b.generatedDate).valueOf()
                                : 0;
                              return da - db;
                            },
                            sortIcon: renderSortIcon,
                            render: (val: string) =>
                              val
                                ? dayjs(val).utc().format('DD/MM/YY HH:mm')
                                : '-',
                          },
                          {
                            title: t(
                              'organisation.reconciliation-sidebar-as-at-label'
                            ).replace(/\s*-\s*$/, ''),
                            dataIndex: 'asAtDate',
                            key: 'asAtDate',
                            width: 130,
                            sorter: (
                              a: (typeof publishedReconciliationReports)[0],
                              b: (typeof publishedReconciliationReports)[0]
                            ) => {
                              const da = a.asAtDate
                                ? dayjs(a.asAtDate).valueOf()
                                : 0;
                              const db = b.asAtDate
                                ? dayjs(b.asAtDate).valueOf()
                                : 0;
                              return da - db;
                            },
                            sortIcon: renderSortIcon,
                            render: (val: string) =>
                              val
                                ? formatDisplayDate(
                                    val,
                                    'DD/MM/YY',
                                    i18n.language
                                  )
                                : '-',
                          },
                          {
                            title: t(
                              'organisation.reconciliation-gentax-balance-column'
                            ),
                            dataIndex: 'gtBalance',
                            key: 'gtBalance',
                            width: 180,
                            sorter: (
                              a: (typeof publishedReconciliationReports)[0],
                              b: (typeof publishedReconciliationReports)[0]
                            ) =>
                              Number(a.gtBalance ?? 0) -
                              Number(b.gtBalance ?? 0),
                            sortIcon: renderSortIcon,
                            render: (val: number | null) =>
                              typeof val === 'number'
                                ? formatWithParens(val)
                                : '-',
                          },
                          {
                            title: t(
                              'organisation.reconciliation-balance-in-provider',
                              {
                                provider:
                                  organisation?.providerName ||
                                  t(
                                    'organisation.reconciliation-provider-placeholder'
                                  ),
                              }
                            ),
                            dataIndex: 'providerBalance',
                            key: 'providerBalance',
                            width: 150,
                            sorter: (
                              a: (typeof publishedReconciliationReports)[0],
                              b: (typeof publishedReconciliationReports)[0]
                            ) =>
                              Number(a.providerBalance ?? 0) -
                              Number(b.providerBalance ?? 0),
                            sortIcon: renderSortIcon,
                            render: (val: number | null) =>
                              typeof val === 'number'
                                ? formatWithParens(val)
                                : '-',
                          },
                          {
                            title: t('organisation.reconciled-value'),
                            dataIndex: 'reconciledValue',
                            key: 'reconciledValue',
                            width: 150,
                            sorter: (
                              a: (typeof publishedReconciliationReports)[0],
                              b: (typeof publishedReconciliationReports)[0]
                            ) =>
                              Number(a.reconciledValue ?? 0) -
                              Number(b.reconciledValue ?? 0),
                            sortIcon: renderSortIcon,
                            render: (val: number) => {
                              if (val === 0) {
                                return (
                                  <span
                                    style={{
                                      fontWeight: 600,
                                      color: 'var(--color-primary, #41aaaf)',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 8,
                                    }}
                                  >
                                    <CheckCircleOutlined
                                      style={{ fontSize: 16 }}
                                    />
                                    {t(
                                      'organisation.reconciliation-reconciled'
                                    )}
                                  </span>
                                );
                              }

                              return (
                                <span
                                  style={{
                                    color: 'var(--color-status-danger, #c12828)',
                                    fontWeight: 600,
                                  }}
                                >
                                  {formatWithParens(val)}
                                </span>
                              );
                            },
                          },
                          {
                            title: t('main.status'),
                            dataIndex: 'status',
                            key: 'status',
                            width: 120,
                            sorter: (
                              a: (typeof publishedReconciliationReports)[0],
                              b: (typeof publishedReconciliationReports)[0]
                            ) =>
                              (a.status || '').localeCompare(
                                b.status || '',
                                undefined,
                                {
                                  sensitivity: 'base',
                                }
                              ),
                            sortIcon: renderSortIcon,
                            render: (status: string) =>
                              status ? (
                                <StatusGetter status={status} noDraft smaller />
                              ) : (
                                '-'
                              ),
                          },
                          {
                            title: t('main.action-required'),
                            key: 'action',
                            width: 100,
                            render: (
                              _: unknown,
                              record: PublishedReconciliationReportRow
                            ) =>
                              record?.reportId ? (
                                <ScopeButton
                                  type="primary"
                                  width="70px"
                                  height="25px"
                                  fontSize="12px"
                                  lineheight="12px"
                                  onClick={() => {
                                    setViewingSavedLogId(
                                      record.reportId ?? null
                                    );
                                    setViewingSavedMappingId(
                                      record.mappingId ?? null
                                    );
                                    setReconciliationSessionStarted(true);
                                  }}
                                >
                                  {t('main.open')}
                                </ScopeButton>
                              ) : null,
                          },
                        ]}
                        mainLocaleKey="users"
                        originalDataSource={
                          filteredPublishedReconciliationReports
                        }
                        tableDataSource={filteredPublishedReconciliationReports}
                        pagination
                        customPagination
                        defaultSort={['generatedDate', 'desc']}
                        total={logsTotalElements}
                        rowsPerPage={preferredLogsPageSize}
                        onPageChange={handleLogsPageChange}
                        enableColumnsCustomization={false}
                        rowHeight={45}
                        titleComponent={
                          <div
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'flex-start',
                            }}
                          >
                            <CardTitleLargeSpan
                              style={{ fontSize: 20, lineHeight: '32px' }}
                            >
                              {t(
                                'organisation.published-reconciliation-reports',
                                'Published reconciliation reports'
                              )}
                            </CardTitleLargeSpan>
                          </div>
                        }
                        scroll={{ x: 1060 }}
                        fontSize={12}
                        showSizeChanger
                        getNewPageSize={saveLogsPageSize}
                        transactionType=""
                        reportId=""
                      />
                    </div>
                  </StyledCard>
                </div>
              </div>
              <EntityNarrowSidePanelChrome
                panelOpen={showReconIntroNarrowPanel}
                onOpenPanel={() => setShowReconIntroNarrowPanel(true)}
                onClosePanel={() => setShowReconIntroNarrowPanel(false)}
                tabLabel={t('organisation.reconciliation-tab')}
                panelWidthPx={TAX_RATES_PROMOTION_SIDE_PANEL_WIDTH}
              >
                {renderReconciliationIntroSidePanel()}
              </EntityNarrowSidePanelChrome>
            </div>
          ) : (
            <div
              style={{
                paddingTop: 8,
                display: 'flex',
                justifyContent: 'space-between',
                width: '100%',
                gap: 20,
                alignContent: 'flex-start',
              }}
            >
              <StyledCard
                style={{
                  width: '100%',
                  minWidth: 600,
                  flex: 1,
                  overflow: 'auto',
                  border: '1px solid #e3ebeb',
                  boxShadow: '0px 4px 10px 0px #0000000D',
                }}
              >
                <div
                  style={{
                    padding: '5px',
                    paddingLeft: '5px',
                    paddingTop: '5px',
                    minWidth: 500,
                    margin: 'auto',
                  }}
                >
                  <ScopeTable
                    className="reconciliation-reports-table"
                    // Keep filter wiring in place, but hide filter UI until BE filtering is ready.
                    columns={[
                      {
                        title: t('organisation.generated-by'),
                        dataIndex: 'generatedBy',
                        key: 'generatedBy',
                        width: 80,
                        className: 'recon-generated-by-col',
                        onHeaderCell: () => ({
                          className: 'recon-generated-by-col',
                        }),
                        onCell: () => ({ className: 'recon-generated-by-col' }),
                        align: 'center',
                        ellipsis: true,
                        sorter: (
                          a: PublishedReconciliationReportRow,
                          b: PublishedReconciliationReportRow
                        ) =>
                          (a.generatedBy || '').localeCompare(
                            b.generatedBy || '',
                            undefined,
                            { sensitivity: 'base' }
                          ),
                        sortIcon: renderSortIcon,
                        render: (
                          _val: string,
                          record: PublishedReconciliationReportRow
                        ) => (
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            {renderAvatar(
                              record.preparerFirstName || '',
                              record.preparerLastName || '',
                              AvatarSizes.SMALL,
                              record.preparerUserId || '',
                              record.preparerProfilePicture
                            )}
                          </div>
                        ),
                      },
                      {
                        title: t('organisation.generated-date'),
                        dataIndex: 'generatedDate',
                        key: 'generatedDate',
                        width: 150,
                        sorter: (
                          a: (typeof publishedReconciliationReports)[0],
                          b: (typeof publishedReconciliationReports)[0]
                        ) => {
                          const da = a.generatedDate
                            ? dayjs(a.generatedDate).valueOf()
                            : 0;
                          const db = b.generatedDate
                            ? dayjs(b.generatedDate).valueOf()
                            : 0;
                          return da - db;
                        },
                        sortIcon: renderSortIcon,
                        render: (val: string) =>
                          val ? dayjs(val).utc().format('DD/MM/YY HH:mm') : '-',
                      },
                      {
                        title: t(
                          'organisation.reconciliation-sidebar-as-at-label'
                        ).replace(/\s*-\s*$/, ''),
                        dataIndex: 'asAtDate',
                        key: 'asAtDate',
                        width: 130,
                        sorter: (
                          a: (typeof publishedReconciliationReports)[0],
                          b: (typeof publishedReconciliationReports)[0]
                        ) => {
                          const da = a.asAtDate
                            ? dayjs(a.asAtDate).valueOf()
                            : 0;
                          const db = b.asAtDate
                            ? dayjs(b.asAtDate).valueOf()
                            : 0;
                          return da - db;
                        },
                        sortIcon: renderSortIcon,
                        render: (val: string) =>
                          val
                            ? formatDisplayDate(val, 'DD/MM/YY', i18n.language)
                            : '-',
                      },
                      {
                        title: t(
                          'organisation.reconciliation-gentax-balance-column'
                        ),
                        dataIndex: 'gtBalance',
                        key: 'gtBalance',
                        width: 180,
                        sorter: (
                          a: (typeof publishedReconciliationReports)[0],
                          b: (typeof publishedReconciliationReports)[0]
                        ) =>
                          Number(a.gtBalance ?? 0) - Number(b.gtBalance ?? 0),
                        sortIcon: renderSortIcon,
                        render: (val: number | null) =>
                          typeof val === 'number' ? formatWithParens(val) : '-',
                      },
                      {
                        title: t(
                          'organisation.reconciliation-balance-in-provider',
                          {
                            provider:
                              organisation?.providerName ||
                              t(
                                'organisation.reconciliation-provider-placeholder'
                              ),
                          }
                        ),
                        dataIndex: 'providerBalance',
                        key: 'providerBalance',
                        width: 150,
                        sorter: (
                          a: (typeof publishedReconciliationReports)[0],
                          b: (typeof publishedReconciliationReports)[0]
                        ) =>
                          Number(a.providerBalance ?? 0) -
                          Number(b.providerBalance ?? 0),
                        sortIcon: renderSortIcon,
                        render: (val: number | null) =>
                          typeof val === 'number' ? formatWithParens(val) : '-',
                      },
                      {
                        title: t('organisation.reconciled-value'),
                        dataIndex: 'reconciledValue',
                        key: 'reconciledValue',
                        width: 150,
                        sorter: (
                          a: (typeof publishedReconciliationReports)[0],
                          b: (typeof publishedReconciliationReports)[0]
                        ) =>
                          Number(a.reconciledValue ?? 0) -
                          Number(b.reconciledValue ?? 0),
                        sortIcon: renderSortIcon,
                        render: (val: number) => {
                          if (val === 0) {
                            return (
                              <span
                                style={{
                                  fontWeight: 600,
                                  color: 'var(--color-primary, #41aaaf)',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: 8,
                                }}
                              >
                                <CheckCircleOutlined style={{ fontSize: 16 }} />
                                {t('organisation.reconciliation-reconciled')}
                              </span>
                            );
                          }

                          return (
                            <span
                              style={{
                                color: 'var(--color-status-danger, #c12828)',
                                fontWeight: 600,
                              }}
                            >
                              {formatWithParens(val)}
                            </span>
                          );
                        },
                      },
                      {
                        title: t('main.status'),
                        dataIndex: 'status',
                        key: 'status',
                        width: 120,
                        sorter: (
                          a: (typeof publishedReconciliationReports)[0],
                          b: (typeof publishedReconciliationReports)[0]
                        ) =>
                          (a.status || '').localeCompare(
                            b.status || '',
                            undefined,
                            {
                              sensitivity: 'base',
                            }
                          ),
                        sortIcon: renderSortIcon,
                        render: (status: string) =>
                          status ? (
                            <StatusGetter status={status} noDraft smaller />
                          ) : (
                            '-'
                          ),
                      },
                      {
                        title: t('main.action-required'),
                        key: 'action',
                        width: 100,
                        render: (
                          _: unknown,
                          record: PublishedReconciliationReportRow
                        ) =>
                          record?.reportId ? (
                            <ScopeButton
                              type="primary"
                              width="70px"
                              height="25px"
                              fontSize="12px"
                              lineheight="12px"
                              onClick={() => {
                                setViewingSavedLogId(record.reportId ?? null);
                                setViewingSavedMappingId(
                                  record.mappingId ?? null
                                );
                                setReconciliationSessionStarted(true);
                              }}
                            >
                              {t('main.open')}
                            </ScopeButton>
                          ) : null,
                      },
                    ]}
                    mainLocaleKey="users"
                    originalDataSource={filteredPublishedReconciliationReports}
                    tableDataSource={filteredPublishedReconciliationReports}
                    pagination
                    customPagination
                    defaultSort={['generatedDate', 'desc']}
                    total={logsTotalElements}
                    rowsPerPage={preferredLogsPageSize}
                    onPageChange={handleLogsPageChange}
                    enableColumnsCustomization={false}
                    rowHeight={45}
                    titleComponent={
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                        }}
                      >
                        <CardTitleLargeSpan
                          style={{ fontSize: 20, lineHeight: '32px' }}
                        >
                          {t(
                            'organisation.published-reconciliation-reports',
                            'Published reconciliation reports'
                          )}
                        </CardTitleLargeSpan>
                      </div>
                    }
                    scroll={{ x: 1060 }}
                    fontSize={12}
                    showSizeChanger
                    getNewPageSize={saveLogsPageSize}
                    transactionType=""
                    reportId=""
                  />
                </div>
              </StyledCard>
              {showReconcileIntroductionPanel &&
                reconciliationWideLayout &&
                renderReconciliationIntroSidePanel()}
            </div>
          ))}
      </div>
    </div>
  );
};
